import type { ReactElement } from "react";
import { AlertTriangle, CheckCircle2, Flag, XCircle } from "lucide-react";
import type { BulkCreateResponse, BulkResult } from "../../../api/types";
import { Button } from "../../../components/ui";

const ICON: Record<BulkResult["outcome"], ReactElement> = {
  CREATED: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
  CREATED_WITH_FLAG: <Flag className="h-4 w-4 fill-signal text-signal" />,
  BLOCKED: <XCircle className="h-4 w-4 text-zinc-400" />,
};
const OUTCOME_LABEL: Record<BulkResult["outcome"], string> = {
  CREATED: "Created",
  CREATED_WITH_FLAG: "Created · needs attention",
  BLOCKED: "Blocked",
};

export function BulkResults({ result, onDone, onOpen }: { result: BulkCreateResponse; onDone: () => void; onOpen: (id: string) => void }) {
  return (
    <div data-testid="bulk-results">
      <div className="mb-5 grid grid-cols-3 gap-3 text-center">
        {([["created", "Created", "text-emerald-300"], ["flagged", "Needs attention", "text-signal"], ["blocked", "Blocked", "text-zinc-300"]] as const).map(([k, l, tone]) => (
          <div key={k} className="rounded-sm border border-white/10 p-3">
            <p data-testid={`bulk-count-${k}`} className={`font-display text-4xl font-bold ${tone}`}>{result[k]}</p>
            <p className="label-caps text-zinc-500">{l}</p>
          </div>
        ))}
      </div>
      {result.past_date && (
        <p data-testid="bulk-past-date-note" className="mb-4 flex items-center gap-2 text-xs text-warn"><AlertTriangle className="h-4 w-4" /> This date is in the past at the venue.</p>
      )}
      <ul className="divide-y divide-white/5 rounded-sm border border-white/10">
        {result.results.map((r) => { const k = r.work_date ? `${r.employee_id}-${r.work_date}` : r.employee_id; return (
          <li key={k} data-testid={`bulk-result-${k}`} className="flex items-start gap-3 px-4 py-3">
            <span className="mt-0.5">{ICON[r.outcome]}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white">{r.employee_name ?? "Unknown employee"}{r.work_date && <span className="ml-2 font-mono text-xs text-zinc-400">{r.work_date}</span>} <span data-testid={`bulk-outcome-${k}`} className="label-caps ml-2 text-zinc-400">{OUTCOME_LABEL[r.outcome]}</span></p>
              <p data-testid={`bulk-message-${k}`} className="mt-0.5 text-xs text-zinc-400">{r.message}</p>
            </div>
            {r.shift && (
              <button type="button" onClick={() => onOpen(r.shift!.id)} data-testid={`bulk-open-${k}`} className="text-xs text-signal hover:text-white">Open</button>
            )}
          </li>
        ); })}
      </ul>
      <div className="mt-6 flex justify-end">
        <Button onClick={onDone} data-testid="bulk-done-button">Done</Button>
      </div>
    </div>
  );
}
